import { useRef, useState } from 'react';
import { usePredictionMarketCategories, useLivePageFindLive } from '@/lib/endpoints';
import { useAppSearchParams } from '@/hooks/useAppSearchParams';
import { useHiddenCategories } from '@/lib/territory/useTerritory';
import type { LiveEventCardDto, MarketOptionDto } from '@/lib/models';
import type { OrderSide } from '@/analytics';
import { BuyInPopup, type BuyMarketContext } from '../BinaryMarkets/BuyInPopup';
import { SnakeLobbyFeed } from '../SnakeMarkets';
import { SubCategoryBar } from '../CategoryTabs/SubCategoryBar';
import { BackToTopButton } from '../Trending/BackToTopButton';
import { LiveHeaderStrip } from './LiveHeaderStrip';
import { LiveCategoryTabsContainer } from './LiveCategoryTabsContainer';
import { LiveGroupAnchors, type LiveGroupAnchor } from './LiveGroupAnchors';
import { LiveGroupSection } from './LiveGroupSection';
import { LiveEmptyState } from './LiveEmptyState';
import { LiveFeedSkeleton } from './LiveFeedSkeleton';
import { useActiveSection } from './useActiveSection';
import {
  eligibleEventCount,
  filterHiddenLiveGroups,
  filterLiveGroupsByCategory,
  isShortMarketsVisible,
  liveNowCount,
  presentCategoryCodes,
  presentSubCategoryCodes,
  toLiveGroups,
} from './live-groups';

/** Popup target carrying the resolved option context + opening side (cf. BinaryLobbyFeed). */
interface BuyTarget {
  context: BuyMarketContext;
  side: OrderSide;
}

// The lobby feed width/grid conventions (cf. BinaryLobbyFeed / Trending).
const FEED_CONTAINER = 'w-full max-w-[760px] xl:max-w-[1300px] px-3.5 md:px-0 mx-auto pb-6';
const FEED_GRID = 'flex flex-col gap-2 md:grid md:grid-cols-2 md:items-stretch md:gap-3 lg:gap-4 xl:grid-cols-3';

const GROUP_TITLES = { closingSoon: 'Closing Soon', today: 'Today', thisWeek: 'This Week' } as const;

/** The four anchorable sections — typing the ref map on this union keeps anchor ids and refs from drifting apart. */
type LiveSectionId = 'shortMarkets' | 'closingSoon' | 'today' | 'thisWeek';

/**
 * The Live page (spec §4-§6): LIVE header strip with the eligible count,
 * category pills (All default), group anchor chips, then the four time groups
 * — Short Markets (the snake widget) and the three card groups from
 * `GET /prediction-market/live`.
 *
 * Client responsibilities, mirroring the lobby's territory model: hide
 * territory-blocked categories, compute the header count (pre pill-filter),
 * and apply the pill filter locally. Grouping, eligibility, and pin ordering
 * come from the server; assignment is per page load (no mid-session moves).
 */
export function LiveFeedContainer() {
  const { data, isLoading, isError } = useLivePageFindLive();
  const { data: categories } = usePredictionMarketCategories();
  const { category, subCategory } = useAppSearchParams();
  const hiddenCategories = useHiddenCategories();
  const [buyTarget, setBuyTarget] = useState<BuyTarget | null>(null);

  const shortMarketsRef = useRef<HTMLElement | null>(null);
  const closingSoonRef = useRef<HTMLElement | null>(null);
  const todayRef = useRef<HTMLElement | null>(null);
  const thisWeekRef = useRef<HTMLElement | null>(null);
  const refs: Record<LiveSectionId, React.RefObject<HTMLElement | null>> = {
    shortMarkets: shortMarketsRef,
    closingSoon: closingSoonRef,
    today: todayRef,
    thisWeek: thisWeekRef,
  };

  // Order of operations (spec §4.1/§6): territory-filter → header count
  // (BEFORE the pill filter — selecting a pill never changes it) → pill filter.
  const visibleGroups = filterHiddenLiveGroups(toLiveGroups(data ?? emptyPayload()), hiddenCategories);
  const shortMarketsCount = data?.shortMarketsCount ?? 0;
  // "N Live Now" counts only in-play events; the eligible total drives the empty state.
  const count = liveNowCount(visibleGroups);
  const eligibleCount = eligibleEventCount(visibleGroups, shortMarketsCount);
  const activeCategory = category ?? null;
  const filtered = filterLiveGroupsByCategory(visibleGroups, activeCategory, subCategory ?? null);
  const showShortMarkets = shortMarketsCount > 0 && isShortMarketsVisible(activeCategory, categories ?? []);
  // Pills/chips only for categories & sub-categories with something live on
  // the page — an empty pill would just show the filtered-empty message.
  const presentCategories = presentCategoryCodes(visibleGroups, shortMarketsCount, categories ?? []);
  const presentSubCategories = activeCategory ? presentSubCategoryCodes(visibleGroups, activeCategory) : undefined;

  const anchors: Array<LiveGroupAnchor & { id: LiveSectionId }> = [
    ...(showShortMarkets ? [{ id: 'shortMarkets' as const, label: 'Short Markets' }] : []),
    ...(['closingSoon', 'today', 'thisWeek'] as const)
      .filter((key) => filtered[key].length > 0)
      .map((key) => ({ id: key, label: GROUP_TITLES[key] })),
  ];
  const activeSection = useActiveSection(anchors.map((a) => ({ id: a.id, ref: refs[a.id] })));

  function scrollToSection(id: string) {
    refs[id as LiveSectionId]?.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  function handleOpenBuy(event: LiveEventCardDto, option: MarketOptionDto, side: OrderSide) {
    setBuyTarget({ context: { ...option, eventName: event.name }, side });
  }

  if (isError) {
    return <div className={`${FEED_CONTAINER} py-6 text-center text-sm text-muted`}>Failed to load live events.</div>;
  }

  if (isLoading) {
    return (
      <div className={FEED_CONTAINER}>
        <LiveFeedSkeleton gridClassName={FEED_GRID} />
      </div>
    );
  }

  const allEmpty = eligibleCount === 0;
  // Distinguish "nothing eligible" from "the pill filtered everything out".
  const filterEmpty = !allEmpty && anchors.length === 0;

  return (
    <div className={`${FEED_CONTAINER} flex flex-col gap-4`}>
      <LiveHeaderStrip count={count} />
      <LiveCategoryTabsContainer presentCategories={presentCategories} />
      <SubCategoryBar allowedCodes={presentSubCategories} />
      <LiveGroupAnchors anchors={anchors} activeId={activeSection} onSelect={scrollToSection} />

      {allEmpty && <LiveEmptyState />}
      {filterEmpty && <div className="py-6 text-center text-sm text-muted">No live events in this category yet.</div>}

      {showShortMarkets && (
        <section ref={shortMarketsRef} className="w-full scroll-mt-24">
          <SnakeLobbyFeed />
        </section>
      )}
      <LiveGroupSection
        title={GROUP_TITLES.closingSoon}
        sectionRef={closingSoonRef}
        events={filtered.closingSoon}
        gridClassName={FEED_GRID}
        onOpenBuy={handleOpenBuy}
      />
      <LiveGroupSection
        title={GROUP_TITLES.today}
        sectionRef={todayRef}
        events={filtered.today}
        gridClassName={FEED_GRID}
        onOpenBuy={handleOpenBuy}
      />
      <LiveGroupSection
        title={GROUP_TITLES.thisWeek}
        sectionRef={thisWeekRef}
        events={filtered.thisWeek}
        gridClassName={FEED_GRID}
        onOpenBuy={handleOpenBuy}
      />

      <BuyInPopup
        market={buyTarget?.context ?? null}
        initialSide={buyTarget?.side ?? 'yes'}
        surface="other" // Live-page analytics events are owned by the data team (spec §2) — no new TradeSurface value here.
        onClose={() => setBuyTarget(null)}
      />
      <BackToTopButton />
    </div>
  );
}

/** Zero-value payload so the empty/error paths share the loaded code path's shapes. */
function emptyPayload() {
  const empty = { events: [], total: 0 };
  return {
    closingSoon: empty,
    today: empty,
    thisWeek: empty,
    shortMarketsCount: 0,
    totalEligibleCount: 0,
    config: { closingSoonMaxMinutes: 10, todayMaxHours: 10, eligibilityMaxHours: 168 },
    lobbyMarketCount: 3,
  };
}
